import { useState } from "react";

import Dropdown from "@/components/ui/dropdown";
import SearchBox from "@/components/ui/searchBox";
import { TimeFilterValue } from "@/types/analysis";

import { Button } from "../ui/button";
import { RangeDateFilter, RangeDateValue } from "./RangeDateFilter";

interface FilterSectionProps {
  searchValue: string;
  onSearchChange: (value: string) => void;

  authorValue?: { label: string; value: string } | null;
  onAuthorSelect: (value: { label: string; value: string } | null) => void;
  authorOptions: { label: string; value: string }[];

  timeValue?:
    | { label: string; value: string }
    | {
        label: string;
        value: string;
        range?: { startDate: string; endDate: string };
      }
    | null;
  onTimeSelect: (
    value:
      | { label: string; value: string }
      | {
          label: string;
          value: string;
          range?: { startDate: string; endDate: string };
        }
      | null
  ) => void;
  disabled?: boolean;
}

export const FilterSection = ({
  searchValue,
  onSearchChange,
  authorValue,
  onAuthorSelect,
  authorOptions,
  timeValue,
  onTimeSelect,
  disabled
}: FilterSectionProps) => {
  const [rangeDate, setRangeDate] = useState<RangeDateValue | null>(null);
  const [showDatepicker, setShowDatepicker] = useState(false);

  const timeOptions = [
    { label: "All", value: TimeFilterValue.All },
    { label: "Today", value: TimeFilterValue.Day },
    { label: "Last 7 days", value: TimeFilterValue.Last7Days },
    { label: "Last 1 month", value: TimeFilterValue.Last1Month },
    { label: "Last 3 months", value: TimeFilterValue.Last3Months },
    { label: "Custom", value: TimeFilterValue.Custom }
  ];

  // Applied custom range (actual saved)
  const appliedRange =
    timeValue?.value === "custom" && "range" in timeValue
      ? timeValue.range
      : null;

  const formattedRangeLabel = appliedRange
    ? `${appliedRange.startDate} to ${appliedRange.endDate}`
    : "Custom";

  const computedTimeValue =
    timeValue?.value === "custom"
      ? { label: formattedRangeLabel, value: "custom" }
      : timeValue || null;

  const handleTimeSelect = (
    option: { label: string; value: string } | null
  ) => {
    if (option?.value !== "custom") {
      setRangeDate(null);
      onTimeSelect(option);
      setShowDatepicker(false);
      return;
    }

    // Open datepicker with current applied range (if any)
    setRangeDate(
      appliedRange
        ? { from: appliedRange.startDate, until: appliedRange.endDate }
        : null
    );

    // Open datepicker for custom
    setShowDatepicker(true);
  };
  return (
    <div className="flex gap-2 items-center relative">
      <SearchBox
        disabled={disabled}
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        placeHolder="Search"
        className="text-[14px]"
        testId="search-box"
        onClear={() => onSearchChange("")}
      />

      <div className="w-[120px]">
        <Dropdown
          className="w-[120px]"
          labelBorder="Author"
          data={authorOptions}
          value={authorValue}
          disabled={disabled}
          onSelect={onAuthorSelect}
          placeholder="Author"
          withSearch
          filterMatchedSearch
        />
      </div>

      <div className="w-[120px]">
        <Dropdown
          className="w-[120px]"
          labelBorder="Time"
          data={timeOptions}
          value={computedTimeValue}
          disabled={disabled}
          onSelect={handleTimeSelect}
          placeholder="Time"
        />
      </div>

      {showDatepicker && (
        <div className="absolute top-full right-0 z-10 bg-white rounded-lg shadow-lg p-2 border mt-2">
          <RangeDateFilter
            defaultValue={{
              from: appliedRange?.startDate || "",
              until: appliedRange?.endDate || ""
            }}
            onChange={setRangeDate}
          />

          <div className="pt-4 flex items-center justify-end gap-2">
            <Button
              className="h-8 w-32 rounded-xl"
              variant="ghost"
              onClick={() => {
                setRangeDate(null);
                setShowDatepicker(false);
              }}
            >
              Cancel
            </Button>

            <Button
              className="h-8 w-32 bg-primary-teal hover:bg-primary-teal/90 text-white rounded-xl"
              disabled={!rangeDate?.from || !rangeDate?.until}
              onClick={() => {
                if (rangeDate) {
                  onTimeSelect({
                    label: `${rangeDate.from} to ${rangeDate.until}`,
                    value: "custom",
                    range: {
                      startDate: rangeDate.from,
                      endDate: rangeDate.until
                    }
                  });
                }

                setRangeDate(null);
                setShowDatepicker(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
